/**
 * Dealer Site Filters
 * Auto-submit on filter changes, make/model dependency, URL building
 * Uses Slim Select for all select dropdowns
 * On mobile, form only submits when sidebar is closed
 */
const DealerFilters = (function () {
    "use strict";

    let nomenclatures = {};
    let baseUrl = "";
    let sidebarOpenUrl = null; // Track URL when mobile sidebar opens

    // Slim Select instances
    let makersSlim = null;
    let modelsSlim = null;
    let fuelSlim = null;
    let bodyTypeSlim = null;
    let gearSlim = null;
    let tvaSlim = null;
    let salestypeSlim = null;

    // Track selection state before dropdown opens
    let openSelection = {};
    let isOpen = {};

    function trackOpen(key, slimInstance) {
        isOpen[key] = true;
        openSelection[key] = slimInstance.getSelected().slice().sort().join(",");
    }

    function submitIfChanged(key, slimInstance) {
        isOpen[key] = false;
        const current = slimInstance.getSelected().slice().sort().join(",");
        if (current !== openSelection[key]) {
            submitForm();
        }
    }

    async function init() {
        const form = document.getElementById("filters-form");
        if (!form) return;

        baseUrl = form.dataset.baseUrl || "";
        const vehicleClass = form.dataset.currentClass || "autoturisme";

        // Fetch nomenclatures via service
        nomenclatures = await NomenclatureService.get(vehicleClass);

        initSlimSelect();
        initClearButtons();
        initFormSubmit();
        initClearFilters();
        initAutoSubmit();
        highlightActiveFilters();
    }

    function initSlimSelect() {
        if (typeof SlimSelect === "undefined") return;

        // Makers
        const makersEl = document.querySelector("#makers-select");
        if (makersEl) {
            makersSlim = new SlimSelect({
                select: "#makers-select",
                settings: {
                    showSearch: true,
                    searchPlaceholder: "Caută",
                    closeOnSelect: false,
                    allowDeselect: true,
                    placeholderText: "Marcă",
                },
                events: {
                    afterChange: (info) => {
                        handleMakersChange(info);
                        highlightActiveFilters();
                        if (!isOpen["makers"]) submitForm();
                    },
                    beforeOpen: () => trackOpen("makers", makersSlim),
                    afterClose: () => submitIfChanged("makers", makersSlim),
                },
            });
        }

        // Models
        const modelsEl = document.querySelector("#models-select");
        if (modelsEl) {
            modelsSlim = new SlimSelect({
                select: "#models-select",
                settings: {
                    showSearch: true,
                    searchPlaceholder: "Caută",
                    closeOnSelect: false,
                    allowDeselect: true,
                    placeholderText: "Model",
                },
                events: {
                    afterChange: (info) => {
                        updateCountBadge("models-count", info.length);
                        highlightActiveFilters();
                        if (!isOpen["models"]) submitForm();
                    },
                    beforeOpen: () => trackOpen("models", modelsSlim),
                    afterClose: () => submitIfChanged("models", modelsSlim),
                },
            });
            if (modelsEl.disabled) {
                modelsSlim.disable();
            }
        }

        // Body Type / Categories
        const bodyTypeEl = document.querySelector("#body-type-select");
        if (bodyTypeEl) {
            bodyTypeSlim = new SlimSelect({
                select: "#body-type-select",
                settings: {
                    showSearch: true,
                    searchPlaceholder: "Caută",
                    closeOnSelect: false,
                    allowDeselect: true,
                    placeholderText: "Caroserie",
                },
                events: {
                    afterChange: (info) => {
                        updateCountBadge("body-type-count", info.length);
                        highlightActiveFilters();
                        if (!isOpen["bodyType"]) submitForm();
                    },
                    beforeOpen: () => trackOpen("bodyType", bodyTypeSlim),
                    afterClose: () => submitIfChanged("bodyType", bodyTypeSlim),
                },
            });
        }

        // Fuel
        const fuelEl = document.querySelector("#fuel-select");
        if (fuelEl) {
            fuelSlim = new SlimSelect({
                select: "#fuel-select",
                settings: {
                    showSearch: true,
                    searchPlaceholder: "Caută",
                    closeOnSelect: false,
                    allowDeselect: true,
                    placeholderText: "Combustibil",
                },
                events: {
                    afterChange: (info) => {
                        updateCountBadge("fuel-count", info.length);
                        highlightActiveFilters();
                        if (!isOpen["fuel"]) submitForm();
                    },
                    beforeOpen: () => trackOpen("fuel", fuelSlim),
                    afterClose: () => submitIfChanged("fuel", fuelSlim),
                },
            });
        }

        // Gearbox
        const gearEl = document.querySelector("#gear-select");
        if (gearEl) {
            gearSlim = new SlimSelect({
                select: "#gear-select",
                settings: {
                    showSearch: false,
                    allowDeselect: true,
                    placeholderText: "Cutie de viteze",
                },
                events: {
                    afterChange: () => {
                        highlightActiveFilters();
                        submitForm();
                    },
                },
            });
        }

        // TVA
        const tvaEl = document.querySelector("#tva-select");
        if (tvaEl) {
            tvaSlim = new SlimSelect({
                select: "#tva-select",
                settings: {
                    showSearch: false,
                    allowDeselect: true,
                    placeholderText: "TVA",
                },
                events: {
                    afterChange: () => {
                        highlightActiveFilters();
                        submitForm();
                    },
                },
            });
        }

        // Availability / salestype
        const salestypeEl = document.querySelector("#salestype-select");
        if (salestypeEl) {
            salestypeSlim = new SlimSelect({
                select: "#salestype-select",
                settings: {
                    showSearch: false,
                    allowDeselect: true,
                    placeholderText: "Disponibilitate",
                },
                events: {
                    afterChange: () => {
                        highlightActiveFilters();
                        submitForm();
                    },
                },
            });
        }
    }

    function handleMakersChange(info) {
        const selected = info.map((item) => item.value);

        // Update count badge
        updateCountBadge("makers-count", selected.length);

        if (selected.length === 1) {
            const makeId = parseInt(selected[0]);
            const make = (nomenclatures.makes || []).find((m) => m.id === makeId);

            if (make && make.models) {
                const modelData = make.models.map((model) => ({
                    text: model.name,
                    value: String(model.id),
                    data: { slug: model.slug },
                }));
                modelsSlim.setData([
                    { placeholder: true, text: "Model" },
                    ...modelData,
                ]);
                modelsSlim.enable();
            }
        } else {
            modelsSlim.setData([{ placeholder: true, text: "Model" }]);
            modelsSlim.disable();
            // Clear models count when makers change
            updateCountBadge("models-count", 0);
        }
    }

    function updateCountBadge(badgeId, count) {
        const badge = document.getElementById(badgeId);
        // Also show/hide the actions container (badge + clear button)
        const actionsId = badgeId.replace("-count", "-actions");
        const actions = document.getElementById(actionsId);

        if (badge) {
            badge.textContent = count;
        }

        if (actions) {
            actions.style.display = count > 0 ? "" : "none";
        }
    }

    function highlightActiveFilters() {
        // Slim Select multi-selects
        const slimMap = [
            { instance: makersSlim, selector: '#makers-select' },
            { instance: modelsSlim, selector: '#models-select' },
            { instance: bodyTypeSlim, selector: '#body-type-select' },
            { instance: fuelSlim, selector: '#fuel-select' },
        ];
        slimMap.forEach(({ instance, selector }) => {
            if (!instance) return;
            const ssMain = document.querySelector(selector)
                ?.closest('.custom-select--slim')?.querySelector('.ss-main');
            if (ssMain) {
                ssMain.classList.toggle('filter-active', instance.getSelected().length > 0);
            }
        });

        // Slim Select single-selects
        const singleMap = [
            { instance: gearSlim, selector: '#gear-select' },
            { instance: tvaSlim, selector: '#tva-select' },
            { instance: salestypeSlim, selector: '#salestype-select' },
        ];
        singleMap.forEach(({ instance, selector }) => {
            if (!instance) return;
            const selected = instance.getSelected();
            const hasValue = selected.length > 0 && selected[0] !== '';
            const ssMain = document.querySelector(selector)
                ?.closest('.custom-select--slim')?.querySelector('.ss-main');
            if (ssMain) {
                ssMain.classList.toggle('filter-active', hasValue);
            }
        });

        // Number inputs
        document.querySelectorAll('.filters-panel .form-control[type="number"]')
            .forEach(input => {
                input.classList.toggle('filter-active', input.value.trim() !== '');
            });

        // 4x4 checkbox
        const tractionCheckbox = document.querySelector('[name="traction"]');
        if (tractionCheckbox) {
            const wrap = tractionCheckbox.closest('.custom-filters-wrap');
            if (wrap) {
                wrap.classList.toggle('filter-active', tractionCheckbox.checked);
            }
        }

        // Toggle single-select clear buttons (X) based on selection
        updateSingleClears();
    }

    function updateSingleClears() {
        const map = [
            { instance: salestypeSlim, actions: "salestype-actions" },
            { instance: gearSlim, actions: "gear-actions" },
            { instance: tvaSlim, actions: "tva-actions" },
        ];
        map.forEach(({ instance, actions }) => {
            const el = document.getElementById(actions);
            if (!el) return;
            let hasValue = false;
            if (instance) {
                const sel = instance.getSelected();
                hasValue = sel.length > 0 && sel[0] !== "";
            }
            el.style.display = hasValue ? "" : "none";
        });
    }

    function initClearButtons() {
        document.querySelectorAll(".filter-clear-btn").forEach((btn) => {
            btn.addEventListener("click", function () {
                const target = this.dataset.clear;
                let cleared = false;

                switch (target) {
                    case "makers":
                        if (makersSlim) {
                            makersSlim.setSelected([]);
                            cleared = true;
                        }
                        break;
                    case "models":
                        if (modelsSlim) {
                            modelsSlim.setSelected([]);
                            cleared = true;
                        }
                        break;
                    case "body-type":
                        if (bodyTypeSlim) {
                            bodyTypeSlim.setSelected([]);
                            cleared = true;
                        }
                        break;
                    case "fuel":
                        if (fuelSlim) {
                            fuelSlim.setSelected([]);
                            cleared = true;
                        }
                        break;
                    case "salestype":
                        if (salestypeSlim) {
                            salestypeSlim.setSelected("");
                            cleared = true;
                        }
                        break;
                    case "gearbox":
                        if (gearSlim) {
                            gearSlim.setSelected("");
                            cleared = true;
                        }
                        break;
                    case "tva":
                        if (tvaSlim) {
                            tvaSlim.setSelected("");
                            cleared = true;
                        }
                        break;
                }

                if (cleared) {
                    highlightActiveFilters();
                    submitForm();
                }
            });
        });
    }

    function initFormSubmit() {
        $("#filters-form").on("submit", function (e) {
            e.preventDefault();
            window.location.href = buildUrl();
        });
    }

    function buildUrl() {
        let url = baseUrl;
        const params = {};

        // Makers (slugs in path for SEO)
        const makerSlugs = getMakerSlugs();
        if (makerSlugs.length) {
            url += "/" + makerSlugs.join(",");

            // Models (only if makers selected)
            const modelSlugs = getModelSlugs();
            if (modelSlugs.length) {
                url += "/" + modelSlugs.join(",");
            }
        }

        // Other filters as query params
        addParam(params, "fuel_id", getSelectedIds(fuelSlim));
        addParam(params, "gearbox_id", getSelectedValue(gearSlim));
        addParam(params, "categories", getSelectedIds(bodyTypeSlim));
        addParam(params, "capacitymin", $('[name="capacitymin"]').val());
        addParam(params, "capacitymax", $('[name="capacitymax"]').val());
        addParam(params, "powermin", $('[name="powermin"]').val());
        addParam(params, "powermax", $('[name="powermax"]').val());
        addParam(params, "firstregistrationyearmin", $('[name="firstregistrationyearmin"]').val());
        addParam(params, "firstregistrationyearmax", $('[name="firstregistrationyearmax"]').val());
        addParam(params, "salespricemin", $('[name="salespricemin"]').val());
        addParam(params, "salespricemax", $('[name="salespricemax"]').val());
        addParam(params, "rollingkmmin", $('[name="rollingkmmin"]').val());
        addParam(params, "rollingkmmax", $('[name="rollingkmmax"]').val());
        addParam(params, "salespricetype", getSelectedValue(tvaSlim));
        addParam(params, "salestype", getSelectedValue(salestypeSlim));

        // 4x4 checkbox
        if ($('[name="traction"]:checked').length) {
            params.traction = "4x4";
        }

        // Build query string
        const qs = Object.entries(params)
            .filter(([_, v]) => v)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join("&");

        return qs ? `${url}?${qs}` : url;
    }

    function getMakerSlugs() {
        if (!makersSlim) return [];
        const selectedValues = makersSlim.getSelected();
        const selectEl = document.querySelector("#makers-select");
        return selectedValues.map((val) => {
            const option = selectEl.querySelector(`option[value="${val}"]`);
            return option?.dataset?.slug || val;
        });
    }

    function getModelSlugs() {
        if (!modelsSlim) return [];
        const selectedValues = modelsSlim.getSelected();
        const data = modelsSlim.getData();
        return selectedValues.map((val) => {
            const item = data.find((d) => d.value === val);
            return item?.data?.slug || val;
        });
    }

    function getSelectedIds(slimInstance) {
        if (!slimInstance) return "";
        const selected = slimInstance.getSelected();
        return selected.length ? selected.join(",") : "";
    }

    function getSelectedValue(slimInstance) {
        if (!slimInstance) return "";
        const selected = slimInstance.getSelected();
        return selected.length ? selected[0] : "";
    }

    function addParam(obj, key, val) {
        if (val) obj[key] = val;
    }

    function submitForm() {
        // On mobile, only submit when sidebar is closed
        const sidebar = document.querySelector('.filters-aside');
        if (sidebar && sidebar.classList.contains('filters-aside--open')) {
            return; // Don't submit while mobile sidebar is open
        }
        window.location.href = buildUrl();
    }

    function initAutoSubmit() {
        // Text inputs - submit on blur only if value changed
        const textInputs = document.querySelectorAll(
            '[name="capacitymin"], [name="capacitymax"], ' +
            '[name="powermin"], [name="powermax"], ' +
            '[name="firstregistrationyearmin"], [name="firstregistrationyearmax"], ' +
            '[name="salespricemin"], [name="salespricemax"], ' +
            '[name="rollingkmmin"], [name="rollingkmmax"]'
        );
        textInputs.forEach(input => {
            // Store original value on focus
            input.addEventListener('focus', function() {
                this.dataset.originalValue = this.value;
            });
            // Only submit if value changed
            input.addEventListener('blur', function() {
                if (this.value !== this.dataset.originalValue) {
                    submitForm();
                }
            });
            // Highlight on input
            input.addEventListener('input', function() {
                highlightActiveFilters();
            });
        });

        // Checkbox - submit on change
        const tractionCheckbox = document.querySelector('[name="traction"]');
        if (tractionCheckbox) {
            tractionCheckbox.addEventListener('change', function() {
                highlightActiveFilters();
                submitForm();
            });
        }

        // Mobile sidebar - track state when opened
        const toggleBtn = document.querySelector('.filters-toggle-btn');
        if (toggleBtn) {
            toggleBtn.addEventListener('click', function() {
                // Store current filter URL when sidebar opens
                sidebarOpenUrl = buildUrl();
            });
        }

        // Mobile sidebar close - submit only if filters changed
        const closeBtn = document.querySelector('.filters-close-btn');
        const overlay = document.querySelector('.filters-overlay');
        const handleSidebarClose = function() {
            setTimeout(function() {
                const currentUrl = buildUrl();
                if (sidebarOpenUrl !== null && currentUrl !== sidebarOpenUrl) {
                    window.location.href = currentUrl;
                }
                sidebarOpenUrl = null;
            }, 50);
        };
        if (closeBtn) {
            closeBtn.addEventListener('click', handleSidebarClose);
        }
        if (overlay) {
            overlay.addEventListener('click', handleSidebarClose);
        }
    }

    function initClearFilters() {
        $(".js-clear-filters").on("click", function () {
            window.location.href = baseUrl;
        });
    }

    return { init };
})();

$(document).ready(function () {
    DealerFilters.init();
});
